import { Minus, Plus } from "lucide-react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { COMPANY_CATEGORIES, CompanyCategory, DEFAULT_LOCATION, OnboardingFormData } from "../onboarding-schema";

interface LocationsStepProps {
  form: UseFormReturn<OnboardingFormData>;
}

const LocationsStep = ({ form }: LocationsStepProps) => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "locations",
  });

  const handleAdd = () => {
    append({ ...DEFAULT_LOCATION });
  };

  const handleRemove = () => {
    if (fields.length > 1) {
      remove(fields.length - 1);
    }
  };

  return (
    <div>
      <p className="text-sm font-medium mb-1" style={{ color: "#0099ce" }}>
        Schritt 3 von 9
      </p>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Wie viele Standorte hat Ihr Unternehmen?</h1>
      <p className="text-sm text-gray-500 mb-6">
        Geben Sie die Anzahl Ihrer Standorte an und wählen Sie für jeden Standort die passenden Branchen aus. So können
        wir die richtigen Berufsgruppen gezielt ansprechen.
      </p>

      {/* Location count stepper */}
      <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8 mb-6">
        <p className="text-sm font-semibold text-gray-900 mb-4">
          Anzahl Standorte <span className="text-red-500">*</span>
        </p>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleRemove}
            disabled={fields.length <= 1}
            className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Minus className="w-4 h-4" />
          </button>
          <span
            className="inline-flex items-center justify-center rounded-full px-5 py-2 text-lg font-bold text-white min-w-[60px]"
            style={{ backgroundColor: "#0099ce" }}
          >
            {fields.length}
          </span>
          <button
            type="button"
            onClick={handleAdd}
            className="flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Per-location category cards */}
      <div className="space-y-6">
        {fields.map((field, index) => (
          <LocationCategoryCard key={field.id} form={form} index={index} />
        ))}
      </div>
    </div>
  );
};

const LocationCategoryCard = ({ form, index }: { form: UseFormReturn<OnboardingFormData>; index: number }) => {
  const categories: CompanyCategory[] = form.watch(`locations.${index}.categories`) ?? [];
  const error = form.formState.errors.locations?.[index]?.categories;

  const toggleCategory = (value: CompanyCategory) => {
    const updated = categories.includes(value) ? categories.filter((c) => c !== value) : [...categories, value];
    form.setValue(`locations.${index}.categories`, updated, {
      shouldValidate: false,
    });
  };

  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-8">
      <p className="text-base font-semibold text-gray-900 mb-1">Standort {index + 1}</p>
      <p className="text-sm text-gray-500 mb-4">Wählen Sie die Branchen, die an diesem Standort vertreten sind.</p>

      <p className="text-sm font-medium text-gray-700 mb-2">
        Branchen <span className="text-red-500">*</span>
      </p>
      <div className="flex flex-wrap gap-2.5">
        {COMPANY_CATEGORIES.map((cat) => {
          const isSelected = categories.includes(cat.value);
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => toggleCategory(cat.value)}
              className={`
                rounded-full px-4 py-2 text-sm font-medium border transition-colors
                ${
                  isSelected
                    ? "text-white border-transparent shadow-sm"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                }
              `}
              style={{ backgroundColor: isSelected ? "#0099ce" : undefined }}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {error && <p className="text-sm font-medium text-destructive mt-2">{error.message}</p>}

      {categories.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            {categories.length} {categories.length === 1 ? "Branche" : "Branchen"} ausgewählt
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationsStep;
